import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Plus, SignOut, TrendUp, CurrencyDollar, X, Sparkle, CaretRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { parseInrInput, formatInrFriendly } from '../lib/inr';
import Modal from '../components/Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PROFILE_BANNER_DISMISS_KEY = 'fz_producer_profile_banner_dismissed';

const ProducerDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [creating, setCreating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem(PROFILE_BANNER_DISMISS_KEY) === '1');

  const [movieForm, setMovieForm] = useState({
    title: '',
    description: '',
    genre: '',
    industry: 'Bollywood',
    budget: '',
    target_amount: '',
    poster_url: 'https://images.unsplash.com/photo-1746107690247-2342dc489c2f?crop=entropy&cs=srgb&fm=jpg&q=85',
    investment_tiers: [
      { name: 'Bronze', min_amount: 1000, royalty_percentage: 1, description: 'Entry level investment' },
      { name: 'Silver', min_amount: 10000, royalty_percentage: 3, description: 'Mid-tier investment' },
      { name: 'Gold', min_amount: 50000, royalty_percentage: 5, description: 'Premium investment' },
    ]
  });

  const [revenueForm, setRevenueForm] = useState({ revenue: '' });

  useEffect(() => {
    if (user) {
      fetchDashboard();
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/producer/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data || {});
    } catch (e) {
      setProfile({});
    }
  };

  const dismissBanner = () => {
    localStorage.setItem(PROFILE_BANNER_DISMISS_KEY, '1');
    setBannerDismissed(true);
  };

  const missingProfileFields = () => {
    if (!profile) return [];
    const missing = [];
    if (!profile.company_name) missing.push('Studio name');
    if (!profile.location) missing.push('Location');
    if (!profile.bio) missing.push('Studio bio');
    if (!profile.website) missing.push('Website');
    return missing;
  };

  const completionPct = () => {
    if (!profile) return 0;
    const total = 4;
    const filled = ['company_name', 'location', 'bio', 'website'].filter(k => !!profile[k]).length;
    return Math.round((filled / total) * 100);
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/dashboard/producer/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMovie = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const budgetNum = parseInrInput(movieForm.budget);
      const targetNum = parseInrInput(movieForm.target_amount);
      if (!Number.isFinite(budgetNum) || budgetNum <= 0) {
        toast.error('Please enter a valid Budget amount');
        return;
      }
      if (!Number.isFinite(targetNum) || targetNum <= 0) {
        toast.error('Please enter a valid Funding Target amount');
        return;
      }
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/movies`,
        {
          ...movieForm,
          budget: budgetNum,
          target_amount: targetNum,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Movie created successfully!');
      setShowCreateModal(false);
      fetchDashboard();
      setMovieForm({
        title: '',
        description: '',
        genre: '',
        industry: 'Bollywood',
        budget: '',
        target_amount: '',
        poster_url: 'https://images.unsplash.com/photo-1746107690247-2342dc489c2f?crop=entropy&cs=srgb&fm=jpg&q=85',
        investment_tiers: [
          { name: 'Bronze', min_amount: 1000, royalty_percentage: 1, description: 'Entry level investment' },
          { name: 'Silver', min_amount: 10000, royalty_percentage: 3, description: 'Mid-tier investment' },
          { name: 'Gold', min_amount: 50000, royalty_percentage: 5, description: 'Premium investment' },
        ]
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create movie');
    } finally {
      setCreating(false);
    }
  };

  const handleDistributeRoyalties = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/movies/${selectedMovie.id}/distribute-royalties`,
        { revenue: parseFloat(revenueForm.revenue) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Royalties distributed successfully!');
      setShowRevenueModal(false);
      setRevenueForm({ revenue: '' });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute royalties');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      
      {/* Navbar */}
      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} data-testid="nav-home-btn" className="flex items-center space-x-3">
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </button>
            <div className="flex items-center space-x-4">
              <button
                data-testid="nav-marketplace-btn"
                onClick={() => navigate('/marketplace')}
                className="btn-secondary"
              >
                Marketplace
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={handleLogout}
                className="btn-secondary flex items-center space-x-2"
              >
                <SignOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Studio profile completion banner */}
        {profile && !bannerDismissed && missingProfileFields().length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-lg mb-8 bg-gradient-to-r from-[#f472b6]/15 via-[#c084fc]/15 to-[#22d3ee]/15 border border-[#c084fc]/40 p-5 sm:p-6"
            data-testid="profile-completion-banner"
          >
            <button
              onClick={dismissBanner}
              data-testid="dismiss-profile-banner"
              aria-label="Dismiss"
              className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            >
              <X size={18} weight="bold" />
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pr-8">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-[#f472b6] via-[#c084fc] to-[#22d3ee] flex items-center justify-center">
                <Sparkle size={24} weight="fill" className="text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-heading text-base sm:text-lg font-bold">Complete your studio profile</h3>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 border border-white/15 font-bold tabular-nums" data-testid="profile-completion-pct">
                    {completionPct()}%
                  </span>
                </div>
                <p className="font-body text-xs sm:text-sm text-slate-300 mb-2">
                  Studios with a complete profile feature on the <span className="text-gold font-bold">Fanzingy landing page</span> — drawing investors to your projects.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-body mr-1 self-center">Still missing:</span>
                  {missingProfileFields().map(f => (
                    <span key={f} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 font-bold">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <button
                  data-testid="complete-profile-cta"
                  onClick={() => navigate('/producer/track-record')}
                  className="btn-primary flex items-center gap-2 text-sm whitespace-nowrap"
                >
                  Complete profile
                  <CaretRight size={14} weight="bold" />
                </button>
              </div>
            </div>
            <div className="absolute left-0 bottom-0 right-0 h-1 bg-white/5">
              <div
                className="h-full bg-gradient-to-r from-[#f472b6] via-[#c084fc] to-[#22d3ee] transition-all"
                style={{ width: `${completionPct()}%` }}
              />
            </div>
          </motion.div>
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-12">
          <div>
            <h1 className="font-heading text-5xl font-bold mb-2">
              PRODUCER <span className="text-gold">DASHBOARD</span>
            </h1>
            <p className="font-body text-xl text-slate-400">Welcome back, {user.name}</p>
          </div>
          <div className="flex gap-2 flex-wrap native-action-row">
            <button
              data-testid="track-record-btn"
              onClick={() => navigate('/producer/track-record')}
              className="btn-secondary flex items-center space-x-2"
            >
              <TrendUp size={18} weight="bold" />
              <span>My Track Record</span>
            </button>
            <button
              data-testid="public-profile-btn"
              onClick={() => navigate(`/producer/${user.id}`)}
              className="btn-secondary flex items-center space-x-2"
            >
              <span>Public Profile</span>
            </button>
            <button
              data-testid="create-movie-btn"
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={20} weight="bold" />
              <span>Create Movie</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div data-testid="stats-grid" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <FilmStrip size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Total Movies</div>
            <div className="font-mono text-3xl font-bold tabular-nums">
              {dashboard?.total_movies || 0}
            </div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <CurrencyDollar size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Total Raised</div>
            <div className="font-mono text-3xl font-bold text-gold tabular-nums">
              ₹{dashboard?.total_raised?.toLocaleString('en-IN') || '0'}
            </div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <TrendUp size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Total Revenue</div>
            <div className="font-mono text-3xl font-bold text-gold tabular-nums">
              ₹{dashboard?.total_revenue?.toLocaleString('en-IN') || '0'}
            </div>
          </div>
        </div>

        {/* Escrow Split */}
        <div data-testid="escrow-split" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <div className="bg-gold/5 border border-gold/30 rounded-lg p-6" data-testid="escrow-held-card">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🔒</span>
              <div className="font-body text-xs text-gold uppercase tracking-wide font-bold">
                In Escrow
              </div>
            </div>
            <div className="font-mono text-2xl font-bold text-gold tabular-nums">
              ₹{(dashboard?.escrow_held || 0).toLocaleString('en-IN')}
            </div>
            <p className="font-body text-xs text-slate-400 mt-2">
              Held until movie reaches funding goal
            </p>
          </div>

          <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-6" data-testid="released-card">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">✓</span>
              <div className="font-body text-xs text-emerald-400 uppercase tracking-wide font-bold">
                Released
              </div>
            </div>
            <div className="font-mono text-2xl font-bold text-emerald-400 tabular-nums">
              ₹{(dashboard?.released || 0).toLocaleString('en-IN')}
            </div>
            <p className="font-body text-xs text-slate-400 mt-2">
              Funds unlocked & available to you
            </p>
          </div>

          <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-6" data-testid="refunded-card">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">↩</span>
              <div className="font-body text-xs text-red-400 uppercase tracking-wide font-bold">
                Refunded
              </div>
            </div>
            <div className="font-mono text-2xl font-bold text-red-400 tabular-nums">
              ₹{(dashboard?.refunded || 0).toLocaleString('en-IN')}
            </div>
            <p className="font-body text-xs text-slate-400 mt-2">
              Returned to backers (goal not met)
            </p>
          </div>
        </div>

        {/* Movies List */}
        <div>
          <h2 className="font-heading text-3xl font-bold mb-6">MY MOVIES</h2>
          {dashboard?.movies?.length === 0 ? (
            <div className="bg-card border border-white/5 rounded-lg p-12 text-center">
              <FilmStrip size={64} className="text-slate-400 mx-auto mb-4" />
              <p className="font-body text-xl text-slate-400 mb-6">No movies created yet</p>
              <button
                data-testid="create-first-movie-btn"
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Your First Movie
              </button>
            </div>
          ) : (
            <div data-testid="movies-list" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dashboard.movies.map((movie) => {
                const fundingPct = Math.min((movie.raised_amount / movie.target_amount) * 100, 100);
                return (
                  <motion.div
                    key={movie.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card border border-white/5 rounded-lg overflow-hidden hover:border-white/10 transition-colors"
                    data-testid={`movie-${movie.id}`}
                  >
                    <div className="aspect-video relative">
                      <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 flex flex-col gap-1.5">
                        <span className="inline-block px-3 py-1 bg-gold text-black rounded-sm font-bold uppercase text-xs">
                          {movie.status}
                        </span>
                        {(() => {
                          const ap = movie.approval_status || 'approved';
                          const styles = {
                            approved: 'bg-emerald-500/85 text-black',
                            pending_approval: 'bg-amber-500/85 text-black',
                            rejected: 'bg-rose-600/85 text-white',
                            draft: 'bg-slate-500/85 text-white',
                          }[ap];
                          const labels = {
                            approved: 'Approved · Live',
                            pending_approval: 'Pending Review',
                            rejected: 'Rejected',
                            draft: 'Draft',
                          }[ap];
                          return (
                            <span
                              data-testid={`movie-approval-status-${movie.id}`}
                              className={`inline-block px-3 py-1 rounded-sm font-bold uppercase text-xs ${styles}`}
                            >
                              {labels}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-2xl font-bold mb-2">{movie.title}</h3>
                      <p className="font-body text-sm text-slate-400 mb-4 line-clamp-2">{movie.description}</p>

                      {movie.approval_status === 'pending_approval' && (
                        <div
                          data-testid={`pending-banner-${movie.id}`}
                          className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 mb-4 font-body text-xs text-amber-200"
                        >
                          ⏳ Awaiting admin review. Your movie is hidden from the marketplace until approved.
                        </div>
                      )}
                      {movie.approval_status === 'rejected' && (
                        <div
                          data-testid={`rejected-banner-${movie.id}`}
                          className="bg-rose-500/10 border border-rose-500/30 rounded-sm p-3 mb-4"
                        >
                          <div className="font-body text-xs font-bold text-rose-200 mb-1">Rejected by admin</div>
                          {movie.rejection_reason && (
                            <div className="font-body text-xs text-rose-300 mb-2">{movie.rejection_reason}</div>
                          )}
                          <button
                            data-testid={`resubmit-btn-${movie.id}`}
                            onClick={async () => {
                              try {
                                await axios.post(
                                  `${BACKEND_URL}/api/admin/movies/${movie.id}/resubmit`,
                                  {},
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                );
                                toast.success('Resubmitted for review');
                                fetchDashboard();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to resubmit');
                              }
                            }}
                            className="px-3 py-1.5 bg-rose-500/20 border border-rose-500/40 text-rose-200 rounded-sm text-xs font-bold uppercase hover:bg-rose-500/30"
                          >
                            Resubmit for review
                          </button>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="font-body text-xs text-slate-400 mb-1">Raised</div>
                          <div className="font-mono text-lg font-bold text-gold tabular-nums">
                            ₹{(movie.raised_amount / 1000).toFixed(0)}K
                          </div>
                        </div>
                        <div>
                          <div className="font-body text-xs text-slate-400 mb-1">Revenue</div>
                          <div className="font-mono text-lg font-bold text-gold tabular-nums">
                            ₹{(movie.revenue / 1000).toFixed(0)}K
                          </div>
                        </div>
                      </div>

                      {/* Per-movie escrow split */}
                      {movie.escrow_breakdown && (
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center" data-testid={`movie-escrow-${movie.id}`}>
                          <div className="bg-gold/5 border border-gold/20 rounded-sm py-2">
                            <div className="font-mono text-sm font-bold text-gold tabular-nums">
                              ₹{((movie.escrow_breakdown.escrow_held || 0) / 1000).toFixed(0)}K
                            </div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wide">🔒 Held</div>
                          </div>
                          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm py-2">
                            <div className="font-mono text-sm font-bold text-emerald-400 tabular-nums">
                              ₹{((movie.escrow_breakdown.released || 0) / 1000).toFixed(0)}K
                            </div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wide">✓ Released</div>
                          </div>
                          <div className="bg-red-500/5 border border-red-500/20 rounded-sm py-2">
                            <div className="font-mono text-sm font-bold text-red-400 tabular-nums">
                              ₹{((movie.escrow_breakdown.refunded || 0) / 1000).toFixed(0)}K
                            </div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wide">↩ Refund</div>
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>Funding Progress</span>
                          <span>{fundingPct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gold transition-all duration-500"
                            style={{ width: `${fundingPct}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          data-testid={`view-movie-${movie.id}-btn`}
                          onClick={() => navigate(`/movie/${movie.id}`)}
                          className="flex-1 btn-secondary py-2 text-sm"
                        >
                          View Details
                        </button>
                        <button
                          data-testid={`funding-setup-${movie.id}-btn`}
                          onClick={() => navigate(`/movie/${movie.id}/funding/setup`)}
                          className="flex-1 btn-secondary py-2 text-sm"
                        >
                          Funding Setup
                        </button>
                        <button
                          data-testid={`manage-crew-${movie.id}-btn`}
                          onClick={() => navigate(`/movie/${movie.id}/crew/manage`)}
                          className="flex-1 btn-secondary py-2 text-sm"
                        >
                          Crew &amp; Cast
                        </button>
                        <button
                          data-testid={`distribute-royalties-${movie.id}-btn`}
                          onClick={() => {
                            setSelectedMovie(movie);
                            setRevenueForm({ revenue: movie.revenue.toString() });
                            setShowRevenueModal(true);
                          }}
                          className="flex-1 btn-primary py-2 text-sm"
                        >
                          Update Revenue
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Movie Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)} title="Create new movie">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-3xl font-bold">CREATE NEW MOVIE</h2>
                <button aria-label="Close dialog"
                  data-testid="close-create-modal-btn"
                  onClick={() => setShowCreateModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateMovie} data-testid="create-movie-form" className="space-y-6">
                <div>
                  <label className="font-body text-sm font-medium mb-2 block">Movie Title</label>
                  <input
                    data-testid="create-movie-title-input"
                    type="text"
                    required
                    value={movieForm.title}
                    onChange={(e) => setMovieForm({ ...movieForm, title: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                    placeholder="The Next Blockbuster"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-2 block">Description</label>
                  <textarea
                    data-testid="create-movie-description-input"
                    required
                    value={movieForm.description}
                    onChange={(e) => setMovieForm({ ...movieForm, description: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm text-white px-4 py-3 font-body outline-none transition-colors"
                    rows={4}
                    placeholder="An epic story about..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium mb-2 block">Genre</label>
                    <input
                      data-testid="create-movie-genre-input"
                      type="text"
                      required
                      value={movieForm.genre}
                      onChange={(e) => setMovieForm({ ...movieForm, genre: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                      placeholder="Action, Drama, Sci-Fi"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium mb-2 block">Industry</label>
                    <select
                      data-testid="create-movie-industry-select"
                      required
                      value={movieForm.industry}
                      onChange={(e) => setMovieForm({ ...movieForm, industry: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                    >
                      <option value="Bollywood">Bollywood</option>
                      <option value="Hollywood">Hollywood</option>
                      <option value="Tollywood">Tollywood</option>
                      <option value="Kollywood">Kollywood</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium mb-2 block">Budget (₹)</label>
                    <input
                      data-testid="create-movie-budget-input"
                      type="text"
                      inputMode="numeric"
                      required
                      value={movieForm.budget}
                      onChange={(e) => setMovieForm({ ...movieForm, budget: e.target.value })}
                      className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                      placeholder="e.g. 50000000  or  5cr  or  50,00,000"
                    />
                    {movieForm.budget && (
                      <p className="text-xs text-gold mt-1 font-mono">
                        = ₹{parseInrInput(movieForm.budget).toLocaleString('en-IN')} ({formatInrFriendly(parseInrInput(movieForm.budget))})
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-2 block">Funding Target (₹)</label>
                  <input
                    data-testid="create-movie-target-input"
                    type="text"
                    inputMode="numeric"
                    required
                    value={movieForm.target_amount}
                    onChange={(e) => setMovieForm({ ...movieForm, target_amount: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                    placeholder="e.g. 10000000  or  1cr  or  10,00,000"
                  />
                  {movieForm.target_amount && (
                    <p className="text-xs text-gold mt-1 font-mono">
                      = ₹{parseInrInput(movieForm.target_amount).toLocaleString('en-IN')} ({formatInrFriendly(parseInrInput(movieForm.target_amount))})
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-2 block">Poster URL</label>
                  <input
                    data-testid="create-movie-poster-input"
                    type="url"
                    required
                    value={movieForm.poster_url}
                    onChange={(e) => setMovieForm({ ...movieForm, poster_url: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    data-testid="cancel-create-btn"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="submit-create-btn"
                    disabled={creating}
                    className="btn-primary"
                  >
                    {creating ? 'Creating...' : 'Create Movie'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </Modal>
      )}

      {/* Revenue Update Modal */}
      {showRevenueModal && selectedMovie && (
        <Modal onClose={() => setShowRevenueModal(false)} title="Update revenue">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 rounded-lg max-w-md w-full"
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-2xl font-bold">UPDATE REVENUE</h2>
                <button aria-label="Close dialog"
                  data-testid="close-revenue-modal-btn"
                  onClick={() => setShowRevenueModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleDistributeRoyalties} data-testid="revenue-form" className="space-y-6">
                <div>
                  <p className="font-body text-slate-400 mb-4">
                    Update revenue for <span className="text-white font-bold">{selectedMovie.title}</span>
                  </p>
                  <label className="font-body text-sm font-medium mb-2 block">Movie Revenue (₹)</label>
                  <input
                    data-testid="revenue-input"
                    type="number"
                    required
                    value={revenueForm.revenue}
                    onChange={(e) => setRevenueForm({ revenue: e.target.value })}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white px-4 font-body outline-none transition-colors"
                    placeholder="5000000"
                  />
                  <p className="font-body text-xs text-slate-400 mt-2">
                    Royalties will be automatically calculated and distributed to all investors
                  </p>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    data-testid="cancel-revenue-btn"
                    onClick={() => setShowRevenueModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="submit-revenue-btn"
                    className="btn-primary"
                  >
                    Distribute Royalties
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </Modal>
      )}
    </div>
  );
};

export default ProducerDashboard;